<script>
  import {
    ChevronLeft,
    ChevronRight,
    Download,
    Minus,
    PanelLeftClose,
    PanelLeftOpen,
    Plus,
    Printer,
    RotateCw,
    Share2,
    X
  } from 'lucide-svelte';
  import { onDestroy, onMount, tick } from 'svelte';
  import Portal from 'svelte-portal';
  import * as pdfjsLib from 'pdfjs-dist';
  import { normalizeUrl } from '$lib/utils';

  export let url = '';
  export let onClose;

  let stageEl;
  let thumbListEl;
  let pageObserver = null;
  let thumbObserver = null;
  let loadingTask = null;
  let pdf = null;
  let resizeTimer = null;
  let noticeTimer = null;
  let realignTimer = null;
  let zoomRenderTimer = null;
  let programmaticScrollTimer = null;
  let programmaticScrollPage = 0;
  let scrollRaf = 0;
  let activeUrl = '';
  let previousBodyOverflow = '';
  let mounted = false;
  let pendingReaderRealign = false;
  let pages = [];
  let totalPages = 0;
  let activePage = 1;
  let pageInput = '1';
  let zoom = 1;
  let rotation = 0;
  let sidebarOpen = true;
  let isLoading = true;
  let isRendering = false;
  let error = '';
  let notice = '';
  let pageCanvases = new Map();
  let pageItems = new Map();
  let visiblePages = new Set();
  let renderedKeys = new Map();
  let renderingKeys = new Map();
  let renderingPages = new Set();
  let pageErrors = new Map();
  let pageSizes = new Map();
  let paintedPageNumbers = new Set();
  let renderTasks = new Map();
  let renderQueue = [];
  let activeRenderCount = 0;
  let thumbCanvases = new Map();
  let thumbItems = new Map();
  let visibleThumbs = new Set();
  let renderedThumbKeys = new Map();
  let renderingThumbKeys = new Map();
  let thumbRenderTasks = new Map();
  let thumbRenderQueue = [];
  let activeThumbRenderCount = 0;

  const MIN_ZOOM = 0.05;
  const MAX_ZOOM = 3;
  const ZOOM_STEP = 0.15;
  const MAX_RENDER_CONCURRENCY = 2;
  const MAX_THUMB_RENDER_CONCURRENCY = 1;
  const MAX_DEVICE_PIXEL_RATIO = 2;
  const MAX_THUMB_DEVICE_PIXEL_RATIO = 1.25;
  const MAX_CANVAS_PIXELS = 12000000;
  const THUMB_WIDTH = 136;
  const SIDEBAR_STORAGE_KEY = 'k2.messenger.pdf.sidebarOpen';

  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

  $: fileUrl = normalizeUrl(url);
  $: fileName = getFileName(fileUrl);
  $: zoomText = `${Math.round(zoom * 100)}%`;
  $: canGoPrev = activePage > 1;
  $: canGoNext = totalPages > 0 && activePage < totalPages;
  $: isFitWidthMode = zoom <= 1.001;

  $: if (mounted && fileUrl && fileUrl !== activeUrl) {
    void loadDocument();
  }

  $: if (mounted && sidebarOpen && totalPages) {
    void refreshThumbSidebar();
  }

  function close() {
    onClose?.();
  }

  function getFileName(value) {
    const fallback = 'document.pdf';
    const raw = String(value || '').trim();
    if (!raw) return fallback;

    try {
      const parsed = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      return safeDecode(parsed.searchParams.get('name') || parsed.pathname.split('/').filter(Boolean).pop() || fallback);
    } catch {
      return safeDecode(raw.split(/[?#]/, 1)[0].split('/').filter(Boolean).pop() || fallback);
    }
  }

  function safeDecode(value) {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function isSameOrigin(targetUrl) {
    if (typeof window === 'undefined') return false;

    try {
      return new URL(targetUrl, window.location.origin).origin === window.location.origin;
    } catch {
      return false;
    }
  }

  function getCredentialMode(targetUrl) {
    return isSameOrigin(targetUrl) ? 'include' : 'omit';
  }

  function readSidebarPreference() {
    if (typeof window === 'undefined') return true;

    try {
      const value = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
      return value === null ? true : value === 'true';
    } catch {
      return true;
    }
  }

  function saveSidebarPreference(value) {
    if (typeof window === 'undefined') return;

    try {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, value ? 'true' : 'false');
    } catch {
      // Storage may be blocked in private mode. The viewer still works for this session.
    }
  }

  function getStageWidth() {
    const fallback = typeof window !== 'undefined' ? window.innerWidth : 1024;
    return Math.max(300, (stageEl?.clientWidth || fallback) - 48);
  }

  function getOutputScale(viewport) {
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO);
    const rawPixels = viewport.width * viewport.height * dpr * dpr;

    if (rawPixels <= MAX_CANVAS_PIXELS) return dpr;
    return Math.max(1, Math.sqrt(MAX_CANVAS_PIXELS / (viewport.width * viewport.height)));
  }

  function getRenderKey(pageNumber) {
    return `${activeUrl}|${pageNumber}|${rotation}|${zoom.toFixed(2)}|${Math.round(getStageWidth())}`;
  }

  function getThumbKey(pageNumber) {
    return `${activeUrl}|thumb|${pageNumber}|${rotation}|${THUMB_WIDTH}`;
  }

  function setRendering(pageNumber, key) {
    renderingKeys.set(pageNumber, key);
    renderingPages.add(pageNumber);
    renderingKeys = new Map(renderingKeys);
    renderingPages = new Set(renderingPages);
    isRendering = renderingPages.size > 0;
  }

  function clearRendering(pageNumber, key) {
    if (renderingKeys.get(pageNumber) === key) {
      renderingKeys.delete(pageNumber);
      renderingPages.delete(pageNumber);
      renderingKeys = new Map(renderingKeys);
      renderingPages = new Set(renderingPages);
      isRendering = renderingPages.size > 0;
    }
  }

  function cancelPageRender(pageNumber) {
    const task = renderTasks.get(pageNumber);
    if (!task) return;

    try {
      task.cancel();
    } catch {
      // Render task may already be settled.
    }

    renderTasks.delete(pageNumber);
  }

  function cancelAllRenderTasks() {
    for (const pageNumber of renderTasks.keys()) {
      cancelPageRender(pageNumber);
    }
  }

  function cancelThumbRender(pageNumber) {
    const task = thumbRenderTasks.get(pageNumber);
    if (!task) return;

    try {
      task.cancel();
    } catch {
      // Render task may already be settled.
    }

    thumbRenderTasks.delete(pageNumber);
  }

  function cancelAllThumbRenderTasks() {
    for (const pageNumber of thumbRenderTasks.keys()) {
      cancelThumbRender(pageNumber);
    }
  }

  function resetRuntimeState() {
    cancelAllRenderTasks();
    cancelAllThumbRenderTasks();
    pageCanvases = new Map();
    pageItems = new Map();
    visiblePages = new Set();
    renderedKeys = new Map();
    renderingKeys = new Map();
    renderingPages = new Set();
    pageErrors = new Map();
    pageSizes = new Map();
    paintedPageNumbers = new Set();
    renderQueue = [];
    activeRenderCount = 0;
    thumbCanvases = new Map();
    thumbItems = new Map();
    visibleThumbs = new Set();
    renderedThumbKeys = new Map();
    renderingThumbKeys = new Map();
    thumbRenderQueue = [];
    activeThumbRenderCount = 0;
    isRendering = false;
  }

  function destroyDocument() {
    clearTimeout(zoomRenderTimer);
    zoomRenderTimer = null;
    pageObserver?.disconnect();
    pageObserver = null;
    thumbObserver?.disconnect();
    thumbObserver = null;
    resetRuntimeState();

    if (loadingTask) {
      try {
        loadingTask.destroy();
      } catch {
        // no-op
      }
    }

    if (pdf) {
      try {
        pdf.destroy();
      } catch {
        // no-op
      }
    }

    loadingTask = null;
    pdf = null;
    pages = [];
    totalPages = 0;
  }

  async function loadDocument() {
    if (!fileUrl) return;

    destroyDocument();
    activeUrl = fileUrl;
    activePage = 1;
    pageInput = '1';
    zoom = 1;
    rotation = 0;
    error = '';
    isLoading = true;

    try {
      loadingTask = pdfjsLib.getDocument({
        url: fileUrl,
        withCredentials: isSameOrigin(fileUrl)
      });

      pdf = await loadingTask.promise;
      totalPages = pdf?.numPages || 0;
      pages = Array.from({ length: totalPages }, (_, index) => index + 1);
      isLoading = false;

      await tick();
      createPageObserver();
      createThumbObserver();
      queueCurrentPages();
      queueCurrentThumbs();
    } catch (err) {
      error =
        err?.name === 'PasswordException'
          ? 'PDF đang được bảo vệ bằng mật khẩu.'
          : err?.name === 'MissingPDFException'
            ? 'Không tìm thấy file PDF này.'
            : 'Không thể mở tài liệu PDF.';
      console.warn('PDF viewer load error:', err);
    } finally {
      loadingTask = null;
      isLoading = false;
    }
  }

  function createPageObserver() {
    pageObserver?.disconnect();

    if (!stageEl || typeof IntersectionObserver === 'undefined') {
      visiblePages = new Set([1, 2, 3].filter((pageNumber) => pageNumber <= totalPages));
      queueCurrentPages();
      return;
    }

    pageObserver = new IntersectionObserver(handlePageIntersections, {
      root: stageEl,
      rootMargin: '900px 0px',
      threshold: [0, 0.01, 0.25, 0.6]
    });

    for (const node of pageItems.values()) {
      pageObserver.observe(node);
    }
  }

  function createThumbObserver() {
    thumbObserver?.disconnect();

    if (!thumbListEl || typeof IntersectionObserver === 'undefined') {
      visibleThumbs = new Set([1, 2, 3, 4, 5].filter((pageNumber) => pageNumber <= totalPages));
      queueCurrentThumbs();
      return;
    }

    thumbObserver = new IntersectionObserver(handleThumbIntersections, {
      root: thumbListEl,
      rootMargin: '520px 0px',
      threshold: [0, 0.01, 0.5]
    });

    for (const node of thumbItems.values()) {
      thumbObserver.observe(node);
    }
  }

  function handlePageIntersections(entries) {
    let changed = false;

    for (const entry of entries) {
      const pageNumber = Number(entry.target?.dataset?.pageNumber);
      if (!pageNumber) continue;

      if (entry.isIntersecting) {
        visiblePages.add(pageNumber);
        changed = true;
      } else if (visiblePages.delete(pageNumber)) {
        changed = true;
      }
    }

    if (changed) {
      visiblePages = new Set(visiblePages);
      updateActivePageFromScroll();
      queueCurrentPages();
    }
  }

  function handleThumbIntersections(entries) {
    let changed = false;

    for (const entry of entries) {
      const pageNumber = Number(entry.target?.dataset?.thumbPageNumber);
      if (!pageNumber) continue;

      if (entry.isIntersecting) {
        visibleThumbs.add(pageNumber);
        changed = true;
      } else if (visibleThumbs.delete(pageNumber)) {
        changed = true;
      }
    }

    if (changed) {
      visibleThumbs = new Set(visibleThumbs);
      queueCurrentThumbs();
    }
  }

  function registerPageItem(node, pageNumber) {
    pageItems.set(pageNumber, node);
    pageItems = new Map(pageItems);

    if (pageObserver) pageObserver.observe(node);

    return {
      destroy() {
        pageObserver?.unobserve(node);
        pageItems.delete(pageNumber);
        pageItems = new Map(pageItems);
      }
    };
  }

  function registerPageCanvas(node, pageNumber) {
    pageCanvases.set(pageNumber, node);
    pageCanvases = new Map(pageCanvases);

    if (pageNumber <= 3 || visiblePages.has(pageNumber)) enqueueRender(pageNumber, 30 - Math.abs(pageNumber - activePage));

    return {
      destroy() {
        cancelPageRender(pageNumber);
        pageCanvases.delete(pageNumber);
        renderedKeys.delete(pageNumber);
        renderingKeys.delete(pageNumber);
        pageCanvases = new Map(pageCanvases);
        renderedKeys = new Map(renderedKeys);
        renderingKeys = new Map(renderingKeys);
      }
    };
  }

  function registerThumbItem(node, pageNumber) {
    thumbItems.set(pageNumber, node);
    thumbItems = new Map(thumbItems);

    if (thumbObserver) thumbObserver.observe(node);

    return {
      destroy() {
        thumbObserver?.unobserve(node);
        thumbItems.delete(pageNumber);
        thumbItems = new Map(thumbItems);
      }
    };
  }

  function registerThumbCanvas(node, pageNumber) {
    thumbCanvases.set(pageNumber, node);
    thumbCanvases = new Map(thumbCanvases);

    if (pageNumber <= 5 || visibleThumbs.has(pageNumber) || Math.abs(pageNumber - activePage) <= 3) {
      enqueueThumbRender(pageNumber, getThumbPriority(pageNumber));
    }

    return {
      destroy() {
        cancelThumbRender(pageNumber);
        thumbCanvases.delete(pageNumber);
        renderedThumbKeys.delete(pageNumber);
        renderingThumbKeys.delete(pageNumber);
        thumbCanvases = new Map(thumbCanvases);
        renderedThumbKeys = new Map(renderedThumbKeys);
        renderingThumbKeys = new Map(renderingThumbKeys);
      }
    };
  }

  function getPriority(pageNumber) {
    return pageNumber === activePage ? 100 : 60 - Math.abs(pageNumber - activePage);
  }

  function getThumbPriority(pageNumber) {
    return pageNumber === activePage ? 80 : 44 - Math.abs(pageNumber - activePage);
  }

  function enqueueRender(pageNumber, priority = getPriority(pageNumber)) {
    if (!pdf || !pageCanvases.has(pageNumber) || pageNumber < 1 || pageNumber > totalPages) return;

    const key = getRenderKey(pageNumber);
    if (renderedKeys.get(pageNumber) === key || renderingKeys.get(pageNumber) === key) return;

    renderQueue = renderQueue.filter((item) => item.pageNumber !== pageNumber);
    renderQueue.push({ pageNumber, priority });
    renderQueue.sort((a, b) => b.priority - a.priority);
    drainRenderQueue();
  }

  function drainRenderQueue() {
    if (!pdf) return;

    while (activeRenderCount < MAX_RENDER_CONCURRENCY && renderQueue.length) {
      const next = renderQueue.shift();
      renderQueue = renderQueue;
      void renderPage(next.pageNumber);
    }
  }

  function enqueueThumbRender(pageNumber, priority = getThumbPriority(pageNumber)) {
    if (!pdf || !thumbCanvases.has(pageNumber) || pageNumber < 1 || pageNumber > totalPages) return;

    const key = getThumbKey(pageNumber);
    if (renderedThumbKeys.get(pageNumber) === key || renderingThumbKeys.get(pageNumber) === key) return;

    thumbRenderQueue = thumbRenderQueue.filter((item) => item.pageNumber !== pageNumber);
    thumbRenderQueue.push({ pageNumber, priority });
    thumbRenderQueue.sort((a, b) => b.priority - a.priority);
    drainThumbRenderQueue();
  }

  function drainThumbRenderQueue() {
    if (!pdf) return;

    while (activeThumbRenderCount < MAX_THUMB_RENDER_CONCURRENCY && thumbRenderQueue.length) {
      const next = thumbRenderQueue.shift();
      thumbRenderQueue = thumbRenderQueue;
      void renderThumb(next.pageNumber);
    }
  }

  async function renderPage(pageNumber) {
    const displayCanvas = pageCanvases.get(pageNumber);
    if (!pdf || !displayCanvas) return;

    const key = getRenderKey(pageNumber);
    if (renderedKeys.get(pageNumber) === key) return;

    activeRenderCount += 1;
    setRendering(pageNumber, key);
    cancelPageRender(pageNumber);

    try {
      const page = await pdf.getPage(pageNumber);
      if (key !== getRenderKey(pageNumber)) return;

      const baseViewport = page.getViewport({ scale: 1, rotation });
      const fitScale = clamp(getStageWidth() / baseViewport.width, 0.35, 3);
      const viewport = page.getViewport({ scale: fitScale * zoom, rotation });
      const outputScale = getOutputScale(viewport);
      const displayWidth = Math.floor(viewport.width);
      const displayHeight = Math.floor(viewport.height);
      const renderCanvas = document.createElement('canvas');
      const ctx = renderCanvas.getContext('2d', { alpha: false }) || renderCanvas.getContext('2d');

      pageSizes.set(pageNumber, {
        width: displayWidth,
        height: displayHeight
      });
      pageSizes = new Map(pageSizes);

      renderCanvas.width = Math.floor(viewport.width * outputScale);
      renderCanvas.height = Math.floor(viewport.height * outputScale);
      ctx.setTransform(outputScale, 0, 0, outputScale, 0, 0);
      ctx.clearRect(0, 0, viewport.width, viewport.height);

      const task = page.render({
        canvasContext: ctx,
        viewport
      });
      renderTasks.set(pageNumber, task);

      await task.promise;

      if (key === getRenderKey(pageNumber)) {
        const currentCanvas = pageCanvases.get(pageNumber);
        if (!currentCanvas) return;

        currentCanvas.width = renderCanvas.width;
        currentCanvas.height = renderCanvas.height;
        currentCanvas.style.width = `${displayWidth}px`;
        currentCanvas.style.height = `${displayHeight}px`;

        const currentCtx = currentCanvas.getContext('2d', { alpha: false }) || currentCanvas.getContext('2d');
        currentCtx.setTransform(1, 0, 0, 1, 0, 0);
        currentCtx.clearRect(0, 0, currentCanvas.width, currentCanvas.height);
        currentCtx.drawImage(renderCanvas, 0, 0);

        renderedKeys.set(pageNumber, key);
        paintedPageNumbers.add(pageNumber);
        pageErrors.delete(pageNumber);
        renderedKeys = new Map(renderedKeys);
        paintedPageNumbers = new Set(paintedPageNumbers);
        pageErrors = new Map(pageErrors);

        if (pendingReaderRealign && pageNumber === activePage) {
          pendingReaderRealign = false;
          void tick().then(() => realignStageToPage(pageNumber));
        }
      }
    } catch (err) {
      if (err?.name !== 'RenderingCancelledException') {
        pageErrors.set(pageNumber, 'Không thể dựng trang này.');
        pageErrors = new Map(pageErrors);
        console.warn('PDF page render error:', err);
      }
    } finally {
      renderTasks.delete(pageNumber);
      clearRendering(pageNumber, key);
      activeRenderCount = Math.max(0, activeRenderCount - 1);
      drainRenderQueue();
    }
  }

  async function renderThumb(pageNumber) {
    const canvas = thumbCanvases.get(pageNumber);
    if (!pdf || !canvas) return;

    const key = getThumbKey(pageNumber);
    if (renderedThumbKeys.get(pageNumber) === key) return;

    activeThumbRenderCount += 1;
    renderingThumbKeys.set(pageNumber, key);
    renderingThumbKeys = new Map(renderingThumbKeys);
    cancelThumbRender(pageNumber);

    try {
      const page = await pdf.getPage(pageNumber);
      if (key !== getThumbKey(pageNumber)) return;

      const baseViewport = page.getViewport({ scale: 1, rotation });
      const viewport = page.getViewport({ scale: THUMB_WIDTH / baseViewport.width, rotation });
      const outputScale = Math.min(window.devicePixelRatio || 1, MAX_THUMB_DEVICE_PIXEL_RATIO);
      const ctx = canvas.getContext('2d', { alpha: false });

      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      ctx.setTransform(outputScale, 0, 0, outputScale, 0, 0);
      ctx.clearRect(0, 0, viewport.width, viewport.height);

      const task = page.render({
        canvasContext: ctx,
        viewport
      });
      thumbRenderTasks.set(pageNumber, task);

      await task.promise;

      if (key === getThumbKey(pageNumber)) {
        renderedThumbKeys.set(pageNumber, key);
        renderedThumbKeys = new Map(renderedThumbKeys);
      }
    } catch (err) {
      if (err?.name !== 'RenderingCancelledException') {
        console.warn('PDF thumbnail render error:', err);
      }
    } finally {
      thumbRenderTasks.delete(pageNumber);
      if (renderingThumbKeys.get(pageNumber) === key) {
        renderingThumbKeys.delete(pageNumber);
        renderingThumbKeys = new Map(renderingThumbKeys);
      }
      activeThumbRenderCount = Math.max(0, activeThumbRenderCount - 1);
      drainThumbRenderQueue();
    }
  }

  function queueCurrentPages() {
    const targets = new Set(visiblePages);

    for (let pageNumber = activePage - 1; pageNumber <= activePage + 2; pageNumber += 1) {
      if (pageNumber >= 1 && pageNumber <= totalPages) targets.add(pageNumber);
    }

    if (!targets.size && totalPages) targets.add(activePage || 1);

    for (const pageNumber of targets) {
      enqueueRender(pageNumber, getPriority(pageNumber));
    }
  }

  function queueCurrentThumbs() {
    const targets = new Set(visibleThumbs);

    for (let pageNumber = activePage - 4; pageNumber <= activePage + 5; pageNumber += 1) {
      if (pageNumber >= 1 && pageNumber <= totalPages) targets.add(pageNumber);
    }

    for (let pageNumber = 1; pageNumber <= Math.min(5, totalPages); pageNumber += 1) {
      targets.add(pageNumber);
    }

    for (const pageNumber of targets) {
      enqueueThumbRender(pageNumber, getThumbPriority(pageNumber));
    }
  }

  function invalidateVisiblePages() {
    cancelAllRenderTasks();
    renderedKeys = new Map();
    renderingKeys = new Map();
    renderingPages = new Set();
    pageErrors = new Map();
    renderQueue = [];
    activeRenderCount = 0;
    isRendering = false;
    queueCurrentPages();
  }

  function rescalePaintedPages(nextZoom, previousZoom) {
    if (!Number.isFinite(nextZoom) || !Number.isFinite(previousZoom) || previousZoom <= 0 || nextZoom === previousZoom) return;
    if (!pageSizes.size) return;

    const ratio = nextZoom / previousZoom;
    const nextSizes = new Map();

    for (const [pageNumber, size] of pageSizes.entries()) {
      const width = Math.max(1, Math.round(size.width * ratio));
      const height = Math.max(1, Math.round(size.height * ratio));
      nextSizes.set(pageNumber, { width, height });

      const canvas = pageCanvases.get(pageNumber);
      if (canvas && paintedPageNumbers.has(pageNumber)) {
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
      }
    }

    pageSizes = nextSizes;
  }

  function getZoomAnchor(pageNumber = activePage) {
    const node = getPageNode(pageNumber);
    if (!stageEl || !node) return null;

    const stageRect = stageEl.getBoundingClientRect();
    const pageRect = node.getBoundingClientRect();
    const focusY = stageRect.top + stageRect.height * 0.42;
    const pageHeight = Math.max(1, pageRect.height);
    const offsetY = clamp(focusY - pageRect.top, 0, pageHeight);

    return {
      pageNumber,
      offsetRatio: offsetY / pageHeight
    };
  }

  function restoreZoomAnchor(anchor) {
    if (!stageEl || !anchor) return;

    const node = getPageNode(anchor.pageNumber);
    if (!node) return;

    const stageRect = stageEl.getBoundingClientRect();
    const pageRect = node.getBoundingClientRect();
    const focusY = stageRect.top + stageRect.height * 0.42;
    const maxTop = Math.max(0, stageEl.scrollHeight - stageEl.clientHeight);
    const targetOffset = Math.max(0, pageRect.height * anchor.offsetRatio);
    const nextTop = stageEl.scrollTop + pageRect.top - focusY + targetOffset;

    markProgrammaticScroll(anchor.pageNumber);
    stageEl.scrollTop = clamp(nextTop, 0, maxTop);
    realignStageToPage(anchor.pageNumber);
  }

  function scheduleZoomRender(targetPage = activePage) {
    clearTimeout(zoomRenderTimer);
    zoomRenderTimer = setTimeout(async () => {
      zoomRenderTimer = null;
      invalidateVisiblePages();
      await tick();
      realignStageToPage(targetPage);
    }, 90);
  }

  function setZoomValue(nextZoom) {
    const targetPage = activePage;
    const zoomAnchor = getZoomAnchor(targetPage);
    const previousZoom = zoom;
    const normalizedZoom = clamp(Number(nextZoom.toFixed(2)), MIN_ZOOM, MAX_ZOOM);

    if (normalizedZoom === previousZoom) {
      void realignReaderAfterLayout();
      return;
    }

    zoom = normalizedZoom;
    rescalePaintedPages(normalizedZoom, previousZoom);

    void tick().then(async () => {
      restoreZoomAnchor(zoomAnchor);
      await nextFrame();
      restoreZoomAnchor(zoomAnchor);
    });

    scheduleZoomRender(targetPage);
  }

  function invalidateThumbs() {
    renderedThumbKeys = new Map();
    renderingThumbKeys = new Map();
    thumbRenderQueue = [];
    queueCurrentThumbs();
  }

  async function refreshThumbSidebar() {
    await tick();
    createThumbObserver();
    queueCurrentThumbs();
    scrollActiveThumbIntoView('auto');
  }

  function getPageNode(pageNumber) {
    return pageItems.get(pageNumber) || stageEl?.querySelector(`[data-page-number="${pageNumber}"]`) || null;
  }

  function getThumbNode(pageNumber) {
    return thumbItems.get(pageNumber) || thumbListEl?.querySelector(`[data-thumb-page-number="${pageNumber}"]`) || null;
  }

  function scrollActiveThumbIntoView(behavior = 'smooth') {
    const target = getThumbNode(activePage);
    if (!thumbListEl || !target) return;

    const listRect = thumbListEl.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const topPadding = 10;
    const bottomPadding = 10;
    let nextTop = thumbListEl.scrollTop;

    if (targetRect.top < listRect.top + topPadding) {
      nextTop += targetRect.top - listRect.top - topPadding;
    } else if (targetRect.bottom > listRect.bottom - bottomPadding) {
      nextTop += targetRect.bottom - listRect.bottom + bottomPadding;
    } else {
      return;
    }

    thumbListEl.scrollTo({
      top: Math.max(0, nextTop),
      behavior
    });
  }

  function nextFrame() {
    if (typeof requestAnimationFrame === 'undefined') return Promise.resolve();
    return new Promise((resolve) => requestAnimationFrame(resolve));
  }

  function realignStageToPage(pageNumber = activePage) {
    if (!stageEl) return;

    const target = getPageNode(pageNumber);
    if (!target) {
      stageEl.scrollLeft = 0;
      return;
    }

    if (isFitWidthMode && stageEl.scrollWidth <= stageEl.clientWidth + 4) {
      stageEl.scrollLeft = 0;
      return;
    }

    const stageRect = stageEl.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const desiredLeft = stageRect.left + Math.max(0, (stageRect.width - targetRect.width) / 2);
    const delta = targetRect.left - desiredLeft;

    if (Math.abs(delta) > 0.5) {
      stageEl.scrollLeft = Math.max(0, stageEl.scrollLeft + delta);
    }
  }

  async function realignReaderAfterLayout() {
    const targetPage = activePage;

    await tick();
    await nextFrame();
    await nextFrame();

    pendingReaderRealign = true;
    invalidateVisiblePages();
    realignStageToPage(targetPage);

    clearTimeout(realignTimer);
    realignTimer = setTimeout(() => {
      realignStageToPage(targetPage);
    }, 260);
  }

  async function setSidebarOpen(value) {
    if (sidebarOpen === value) {
      await realignReaderAfterLayout();
      return;
    }

    sidebarOpen = value;
    saveSidebarPreference(value);

    if (!value) {
      thumbObserver?.disconnect();
      thumbObserver = null;
    }

    await realignReaderAfterLayout();

    if (value) {
      await refreshThumbSidebar();
    }
  }

  function toggleSidebar() {
    void setSidebarOpen(!sidebarOpen);
  }

  function updateActivePageFromScroll() {
    if (!stageEl || !totalPages) return;

    const stageRect = stageEl.getBoundingClientRect();
    let bestPage = activePage;
    let bestDistance = Number.POSITIVE_INFINITY;
    const nodes = pageItems.size
      ? Array.from(pageItems.entries())
      : Array.from(stageEl.querySelectorAll('[data-page-number]')).map((node) => [
          Number(node.dataset.pageNumber),
          node
        ]);

    for (const [pageNumber, node] of nodes) {
      if (!pageNumber || !node) continue;
      const rect = node.getBoundingClientRect();
      if (rect.bottom < stageRect.top + 24 || rect.top > stageRect.bottom - 24) continue;

      const distance = Math.abs(rect.top - stageRect.top - 18);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestPage = pageNumber;
      }
    }

    if (bestPage !== activePage) {
      activePage = bestPage;
      pageInput = String(bestPage);
      queueCurrentThumbs();
      scrollActiveThumbIntoView();
    }
  }

  function handleStageScroll() {
    if (scrollRaf) return;

    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      if (programmaticScrollPage) {
        queueCurrentPages();
        return;
      }
      updateActivePageFromScroll();
      queueCurrentPages();
    });
  }

  function getStageScrollTopForPage(pageNumber) {
    const target = getPageNode(pageNumber);
    if (!stageEl || !target) return null;

    const stageRect = stageEl.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const maxTop = Math.max(0, stageEl.scrollHeight - stageEl.clientHeight);
    const nextTop = stageEl.scrollTop + targetRect.top - stageRect.top - 16;

    return clamp(nextTop, 0, maxTop);
  }

  function markProgrammaticScroll(pageNumber) {
    programmaticScrollPage = pageNumber;
    clearTimeout(programmaticScrollTimer);
    programmaticScrollTimer = setTimeout(() => {
      programmaticScrollPage = 0;
      updateActivePageFromScroll();
    }, 180);
  }

  function scrollStageToPage(pageNumber, behavior = 'auto') {
    if (!stageEl) return false;

    const nextTop = getStageScrollTopForPage(pageNumber);
    if (nextTop === null) return false;

    markProgrammaticScroll(pageNumber);

    if (behavior === 'smooth' && typeof stageEl.scrollTo === 'function') {
      stageEl.scrollTo({ top: nextTop, behavior });
    } else {
      stageEl.scrollTop = nextTop;
    }

    return true;
  }

  async function scrollToPage(pageNumber, behavior = 'auto') {
    const target = clamp(Number(pageNumber) || 1, 1, totalPages || 1);
    activePage = target;
    pageInput = String(target);

    await tick();

    const didScroll = scrollStageToPage(target, behavior);
    queueCurrentPages();
    queueCurrentThumbs();
    scrollActiveThumbIntoView('auto');

    await nextFrame();
    if (didScroll && behavior !== 'smooth') {
      const nextTop = getStageScrollTopForPage(target);
      if (nextTop !== null && Math.abs(stageEl.scrollTop - nextTop) > 2) {
        stageEl.scrollTop = nextTop;
      }
    }
    realignStageToPage(target);
  }

  function submitPage() {
    void scrollToPage(pageInput);
  }

  function prevPage() {
    if (canGoPrev) void scrollToPage(activePage - 1);
  }

  function nextPage() {
    if (canGoNext) void scrollToPage(activePage + 1);
  }

  function zoomIn() {
    setZoomValue(zoom + ZOOM_STEP);
  }

  function zoomOut() {
    setZoomValue(zoom - ZOOM_STEP);
  }

  function fitWidth() {
    setZoomValue(1);
  }

  function rotateRight() {
    rotation = (rotation + 90) % 360;
    invalidateVisiblePages();
    invalidateThumbs();
  }

  function showNotice(message) {
    notice = message;
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => {
      notice = '';
    }, 2200);
  }

  function triggerDownload(href, downloadName) {
    const link = document.createElement('a');
    link.href = href;
    link.download = downloadName;
    link.rel = 'noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  async function downloadPdf() {
    if (!fileUrl) return;

    try {
      const response = await fetch(fileUrl, { credentials: getCredentialMode(fileUrl) });
      if (!response.ok) throw new Error('download_failed');

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, fileName);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1200);
    } catch {
      triggerDownload(fileUrl, fileName);
    }
  }

  async function sharePdf() {
    if (!fileUrl) return;

    try {
      if (navigator.share) {
        const response = await fetch(fileUrl, { credentials: getCredentialMode(fileUrl) }).catch(() => null);
        const blob = response?.ok ? await response.blob() : null;

        if (blob) {
          const file = new File([blob], fileName, { type: blob.type || 'application/pdf' });
          if (!navigator.canShare || navigator.canShare({ files: [file] })) {
            await navigator.share({ title: fileName, text: fileName, files: [file] });
            return;
          }
        }

        await navigator.share({ title: fileName, text: fileName, url: fileUrl });
        return;
      }

      await navigator.clipboard?.writeText(fileUrl);
      showNotice('Đã copy liên kết PDF.');
    } catch (err) {
      if (err?.name === 'AbortError') return;

      try {
        await navigator.clipboard?.writeText(fileUrl);
        showNotice('Đã copy liên kết PDF.');
      } catch {
        showNotice('Không thể chia sẻ PDF trên thiết bị này.');
      }
    }
  }

  async function printPdf() {
    if (!fileUrl) return;

    showNotice('Đang chuẩn bị in PDF...');

    let printUrl = fileUrl;
    let objectUrl = '';
    const frame = document.createElement('iframe');
    let completed = false;

    try {
      const response = await fetch(fileUrl, { credentials: getCredentialMode(fileUrl) });
      if (response.ok) {
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        printUrl = objectUrl;
      }
    } catch {
      // Fallback to the original URL. Some endpoints do not allow fetch/CORS.
    }

    frame.style.position = 'fixed';
    frame.style.left = '-10000px';
    frame.style.top = '0';
    frame.style.width = '1px';
    frame.style.height = '1px';
    frame.style.border = '0';
    frame.style.opacity = '0';
    frame.src = printUrl;

    const cleanup = () => {
      setTimeout(() => {
        frame.remove();
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }, 1000);
    };

    const fallback = () => {
      if (completed) return;
      completed = true;
      cleanup();
      showNotice('Trình duyệt chặn lệnh in. Hãy thử tải PDF rồi in từ máy.');
    };

    const fallbackTimer = setTimeout(fallback, 4500);

    frame.onload = () => {
      if (completed) return;
      setTimeout(() => {
        if (completed) return;
        completed = true;
        clearTimeout(fallbackTimer);

        try {
          frame.contentWindow?.focus();
          frame.contentWindow?.print();
          showNotice('Đã gửi lệnh in PDF.');
          cleanup();
        } catch {
          fallback();
        }
      }, 250);
    };

    document.body.appendChild(frame);
  }

  function getPageStyle(pageNumber) {
    const size = pageSizes.get(pageNumber);
    if (!size) return '';
    return `width:${size.width}px;height:${size.height}px;`;
  }

  function handleKey(event) {
    const target = event.target;
    const isTyping =
      target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;

    if (event.key === 'Escape') {
      close();
      return;
    }

    if (isTyping) return;
    if (event.key === 'ArrowLeft') prevPage();
    if (event.key === 'ArrowRight') nextPage();
    if (event.key === '+' || event.key === '=') zoomIn();
    if (event.key === '-') zoomOut();
    if (event.key.toLowerCase() === 'r') rotateRight();
    if (event.key === '0') fitWidth();
  }

  function handleResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      invalidateVisiblePages();
      realignStageToPage(activePage);
    }, 140);
  }

  onMount(() => {
    mounted = true;
    sidebarOpen = readSidebarPreference();
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    window.addEventListener('resize', handleResize);
    void loadDocument();
  });

  onDestroy(() => {
    mounted = false;
    clearTimeout(resizeTimer);
    clearTimeout(noticeTimer);
    clearTimeout(realignTimer);
    clearTimeout(zoomRenderTimer);
    clearTimeout(programmaticScrollTimer);
    if (scrollRaf) cancelAnimationFrame(scrollRaf);
    window.removeEventListener('keydown', handleKey);
    window.removeEventListener('resize', handleResize);
    document.body.style.overflow = previousBodyOverflow;
    destroyDocument();
  });
</script>

<Portal>
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="pdf-modal" role="dialog" aria-modal="true" aria-label={`Đọc PDF ${fileName}`} on:click|self={close}>
    <section class="modal-card">
      <header class="modal-header">
        <div class="title-block">
          <span>PDF</span>
          <strong title={fileName}>{fileName}</strong>
          <small>Cuộn liên tục · Trang {totalPages ? activePage : '--'} / {totalPages || '--'} · {zoomText}</small>
        </div>

        <div class="header-actions" aria-label="Tác vụ PDF">
          <button
            type="button"
            title={sidebarOpen ? 'Ẩn danh sách trang' : 'Hiện danh sách trang'}
            aria-label={sidebarOpen ? 'Ẩn danh sách trang' : 'Hiện danh sách trang'}
            on:click={toggleSidebar}>
            {#if sidebarOpen}
              <PanelLeftClose size={17} />
            {:else}
              <PanelLeftOpen size={17} />
            {/if}
          </button>
          <button type="button" title="Thu nhỏ" aria-label="Thu nhỏ" on:click={zoomOut} disabled={zoom <= MIN_ZOOM}>
            <Minus size={17} />
          </button>
          <button type="button" class="text-action" title="Vừa chiều rộng" aria-label="Vừa chiều rộng" on:click={fitWidth}>
            {zoomText}
          </button>
          <button type="button" title="Phóng to" aria-label="Phóng to" on:click={zoomIn} disabled={zoom >= MAX_ZOOM}>
            <Plus size={17} />
          </button>
          <button type="button" title="Xoay trang" aria-label="Xoay trang" on:click={rotateRight}>
            <RotateCw size={17} />
          </button>
          <button type="button" title="In PDF" aria-label="In PDF" on:click={printPdf}>
            <Printer size={17} />
          </button>
          <button type="button" title="Chia sẻ PDF" aria-label="Chia sẻ PDF" on:click={sharePdf}>
            <Share2 size={17} />
          </button>
          <button type="button" title="Tải PDF" aria-label="Tải PDF" on:click={downloadPdf}>
            <Download size={17} />
          </button>
          <button type="button" class="close-button" title="Đóng" aria-label="Đóng" on:click={close}>
            <X size={18} />
          </button>
        </div>
      </header>

      <nav class="page-toolbar" aria-label="Điều hướng trang PDF">
        <button type="button" class="nav-prev" disabled={!canGoPrev} on:click|preventDefault|stopPropagation={prevPage}>
          <ChevronLeft size={17} />
          <span>Trang trước</span>
        </button>

        <form class="page-form" on:submit|preventDefault={submitPage}>
          <span>Đến trang</span>
          <input
            inputmode="numeric"
            type="number"
            min="1"
            max={totalPages || 1}
            value={pageInput}
            aria-label="Nhập số trang"
            on:input={(event) => (pageInput = event.currentTarget.value)} />
          <span>/ {totalPages || '--'}</span>
        </form>

        <button type="button" class="nav-next" disabled={!canGoNext} on:click|preventDefault|stopPropagation={nextPage}>
          <span>Trang sau</span>
          <ChevronRight size={17} />
        </button>
      </nav>

      <div class="reader-layout" class:reader-layout--sidebar={sidebarOpen}>
        {#if sidebarOpen}
          <aside class="page-sidebar" aria-label="Chọn nhanh trang PDF">
            <div class="sidebar-title">
              <strong>Trang</strong>
              <span>{totalPages || 0}</span>
            </div>

            <div bind:this={thumbListEl} class="page-list">
              {#each pages as pageNumber (pageNumber)}
                <button
                  type="button"
                  class="thumb-button"
                  class:active={pageNumber === activePage}
                  data-thumb-page-number={pageNumber}
                  on:click|preventDefault|stopPropagation={() => scrollToPage(pageNumber, 'auto')}
                  aria-current={pageNumber === activePage ? 'page' : undefined}
                  aria-label={`Đến trang ${pageNumber}`}
                  use:registerThumbItem={pageNumber}>
                  <span class="thumb-canvas-shell">
                    <canvas
                      class:hidden-thumb={!renderedThumbKeys.has(pageNumber)}
                      aria-hidden="true"
                      use:registerThumbCanvas={pageNumber}>
                    </canvas>

                    {#if !renderedThumbKeys.has(pageNumber)}
                      <span class="thumb-placeholder">Trang {pageNumber}</span>
                    {/if}
                  </span>
                  <span class="thumb-page-number">{pageNumber}</span>
                </button>
              {/each}
            </div>
          </aside>
        {/if}

        <main
          bind:this={stageEl}
          class:reader-stage--fit={isFitWidthMode}
          class="reader-stage"
          aria-busy={isLoading || isRendering}
          on:scroll={handleStageScroll}>
          {#if isLoading}
            <div class="floating-state">Đang mở tài liệu...</div>
          {:else if isRendering}
            <div class="floating-state">Đang dựng trang...</div>
          {/if}

          {#if error}
            <div class="error-state">
              <strong>{error}</strong>
              <span>Hãy kiểm tra file hoặc thử mở lại tài liệu từ server.</span>
            </div>
          {/if}

          {#if !error && !isLoading}
            <div class:continuous-pages--fit={isFitWidthMode} class="continuous-pages">
              {#each pages as pageNumber (pageNumber)}
                <article
                  class="page-card"
                  class:page-card--active={pageNumber === activePage}
                  class:page-card--sized={pageSizes.has(pageNumber)}
                  data-page-number={pageNumber}
                  style={getPageStyle(pageNumber)}
                  use:registerPageItem={pageNumber}>
                  <canvas
                    class:hidden-canvas={!paintedPageNumbers.has(pageNumber)}
                    aria-label={`Trang ${pageNumber}`}
                    use:registerPageCanvas={pageNumber}>
                  </canvas>

                  {#if pageErrors.has(pageNumber) || !paintedPageNumbers.has(pageNumber)}
                    <div class="page-placeholder">
                      {#if pageErrors.has(pageNumber)}
                        <strong>{pageErrors.get(pageNumber)}</strong>
                        <button type="button" on:click={() => enqueueRender(pageNumber, 120)}>Thử lại</button>
                      {:else}
                        <span>Trang {pageNumber}</span>
                      {/if}
                    </div>
                  {/if}
                </article>
              {/each}
            </div>
          {/if}
        </main>
      </div>

      {#if notice}
        <div class="notice" role="status">{notice}</div>
      {/if}
    </section>
  </div>
</Portal>

<style>
  .pdf-modal {
    position: fixed;
    inset: 0;
    z-index: 2147483000;
    display: grid;
    place-items: center;
    padding: 0.5rem;
    background: rgba(15, 23, 42, 0.64);
  }

  .modal-card {
    position: relative;
    display: grid;
    grid-template-rows: auto auto minmax(0, 1fr);
    width: min(96rem, calc(100vw - 1rem));
    height: calc(100dvh - 1rem);
    min-height: 28rem;
    overflow: hidden;
    border: 1px solid rgba(203, 213, 225, 0.92);
    border-radius: 0.8rem;
    background: #f8fafc;
    box-shadow: 0 28px 74px rgba(15, 23, 42, 0.36);
  }

  .modal-header,
  .page-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.9rem;
    padding: 0.72rem 1rem;
    border-bottom: 1px solid #dbe3ec;
    background: rgba(255, 255, 255, 0.96);
  }

  .title-block {
    display: grid;
    min-width: 0;
    gap: 0.18rem;
  }

  .title-block span {
    width: fit-content;
    padding: 0.18rem 0.42rem;
    border-radius: 0.32rem;
    color: #b42318;
    background: #fee4e2;
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 0.02em;
  }

  .title-block strong {
    overflow: hidden;
    color: #172033;
    font-size: 0.96rem;
    font-weight: 750;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .title-block small {
    color: #64748b;
    font-size: 0.76rem;
    line-height: 1.35;
  }

  .header-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    min-width: 0;
    overflow-x: auto;
    padding-bottom: 0.04rem;
  }

  .header-actions button,
  .page-toolbar button,
  .page-placeholder button {
    appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    min-width: 2.35rem;
    height: 2.35rem;
    border: 1px solid #cbd5e1;
    border-radius: 0.62rem;
    color: #1f2937;
    background: #fff;
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .header-actions button:hover,
  .page-toolbar button:hover,
  .page-placeholder button:hover {
    border-color: #94a3b8;
    background: #f8fafc;
  }

  .header-actions button:disabled,
  .page-toolbar button:disabled {
    cursor: not-allowed;
    opacity: 0.46;
  }

  .header-actions .text-action {
    min-width: 4.1rem;
    padding: 0 0.68rem;
    font-size: 0.78rem;
    letter-spacing: 0.01em;
  }

  .close-button {
    color: #b42318 !important;
  }

  .page-toolbar {
    justify-content: center;
    gap: 0.8rem;
    padding-inline: 1rem;
    background: #f8fafc;
  }

  .page-toolbar .nav-prev {
    padding-left: 0.9rem;
    padding-right: 1rem;
  }

  .page-toolbar .nav-next {
    padding-left: 1rem;
    padding-right: 0.9rem;
  }

  .page-form {
    display: inline-flex;
    align-items: center;
    gap: 0.56rem;
    height: 2.35rem;
    padding: 0 0.78rem;
    border: 1px solid #cbd5e1;
    border-radius: 0.62rem;
    color: #475569;
    background: #fff;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .page-form input {
    width: 4.8rem;
    height: 1.72rem;
    border: 1px solid #cbd5e1;
    border-radius: 0.44rem;
    color: #111827;
    text-align: center;
    font: inherit;
    font-weight: 700;
    background: #fff;
  }

  .reader-layout {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    min-height: 0;
    overflow: hidden;
  }

  .reader-layout--sidebar {
    grid-template-columns: 14.75rem minmax(0, 1fr);
  }

  .page-sidebar {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    min-height: 0;
    border-right: 1px solid #dbe3ec;
    background: #f8fafc;
  }

  .sidebar-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.75rem 0.8rem;
    border-bottom: 1px solid #dbe3ec;
    color: #172033;
    font-size: 0.78rem;
  }

  .sidebar-title span {
    color: #64748b;
    font-weight: 700;
  }

  .page-list {
    display: grid;
    align-content: start;
    gap: 0.86rem;
    min-height: 0;
    overflow: auto;
    padding: 0.78rem 0.82rem 1rem;
  }

  .thumb-button {
    appearance: none;
    display: grid;
    justify-items: center;
    gap: 0.42rem;
    min-width: 0;
    padding: 0.42rem;
    border: 1px solid transparent;
    border-radius: 0.72rem;
    color: #334155;
    background: transparent;
    font-size: 0.82rem;
    font-weight: 750;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .thumb-button:hover {
    background: #eef4ff;
  }

  .thumb-button.active {
    border-color: #0b74ff;
    color: #fff;
    background: #0b74ff;
    box-shadow: 0 8px 20px rgba(11, 116, 255, 0.22);
  }

  .thumb-canvas-shell {
    position: relative;
    display: grid;
    place-items: center;
    width: 100%;
    min-height: 11.4rem;
    overflow: hidden;
    border-radius: 0.5rem;
    background: #fff;
    box-shadow:
      inset 0 0 0 1px rgba(203, 213, 225, 0.72),
      0 6px 16px rgba(15, 23, 42, 0.08);
  }

  .thumb-button.active .thumb-canvas-shell {
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.78);
  }

  .thumb-canvas-shell canvas {
    max-width: 100%;
    height: auto;
  }

  .hidden-thumb {
    opacity: 0;
  }

  .thumb-placeholder {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    color: #94a3b8;
    background: linear-gradient(180deg, #fff, #f8fafc);
    font-size: 0.72rem;
    font-weight: 800;
  }

  .thumb-page-number {
    color: #64748b;
    line-height: 1;
  }

  .thumb-button.active .thumb-page-number {
    color: #fff;
  }

  .reader-stage {
    position: relative;
    min-width: 0;
    min-height: 0;
    overflow: auto;
    overscroll-behavior: contain;
    background: #e9eef5;
  }

  .reader-stage--fit {
    overflow-x: hidden;
  }

  .continuous-pages {
    display: grid;
    justify-items: center;
    gap: 1rem;
    min-width: min-content;
    padding: 1rem;
  }

  .continuous-pages--fit {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
  }

  .page-card {
    position: relative;
    display: grid;
    place-items: center;
    width: fit-content;
    height: fit-content;
    min-width: 18rem;
    min-height: 24rem;
    border: 1px solid transparent;
    background: #fff;
    box-shadow: 0 14px 34px rgba(15, 23, 42, 0.18);
    scroll-margin-top: 0.75rem;
    transition:
      width 140ms ease,
      height 140ms ease;
    will-change: width, height;
  }

  .page-card--sized {
    min-width: 0;
    min-height: 0;
  }

  .page-card--active {
    border-color: rgba(37, 99, 235, 0.42);
  }

  canvas {
    display: block;
    max-width: none;
    background: #fff;
    transition:
      width 140ms ease,
      height 140ms ease;
  }

  .hidden-canvas {
    opacity: 0;
  }

  .page-placeholder {
    position: absolute;
    inset: 0;
    display: grid;
    place-content: center;
    justify-items: center;
    gap: 0.58rem;
    color: #64748b;
    background: linear-gradient(180deg, #fff, #f8fafc);
    font-size: 0.78rem;
    font-weight: 700;
  }

  .page-placeholder strong {
    color: #b42318;
  }

  .floating-state {
    position: sticky;
    top: 0.75rem;
    left: 50%;
    z-index: 2;
    width: fit-content;
    margin: 0.75rem auto -2.4rem;
    padding: 0.42rem 0.72rem;
    border: 1px solid #dbe3ec;
    border-radius: 999px;
    color: #172033;
    background: rgba(255, 255, 255, 0.95);
    font-size: 0.76rem;
    font-weight: 700;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.1);
  }

  .error-state {
    position: absolute;
    inset: 1rem;
    z-index: 3;
    display: grid;
    place-content: center;
    justify-items: center;
    gap: 0.55rem;
    padding: 1rem;
    border: 1px solid #dbe3ec;
    border-radius: 0.86rem;
    text-align: center;
    background: rgba(255, 255, 255, 0.96);
  }

  .error-state strong {
    color: #172033;
    font-size: 0.96rem;
  }

  .error-state span {
    color: #64748b;
    font-size: 0.78rem;
  }

  .notice {
    position: absolute;
    left: 50%;
    bottom: 1rem;
    transform: translateX(-50%);
    padding: 0.48rem 0.78rem;
    border-radius: 999px;
    color: #fff;
    background: rgba(17, 24, 39, 0.9);
    font-size: 0.76rem;
    font-weight: 700;
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.24);
  }

  @media (max-width: 760px) {
    .pdf-modal {
      padding: 0;
    }

    .modal-card {
      width: 100vw;
      height: 100dvh;
      min-height: 0;
      border-radius: 0;
    }

    .modal-header {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 0.52rem;
      padding: 0.62rem;
    }

    .header-actions {
      justify-content: flex-start;
    }

    .page-toolbar {
      gap: 0.42rem;
      padding: 0.54rem 0.62rem;
    }

    .page-toolbar button span {
      display: none;
    }

    .page-form {
      flex: 1;
      justify-content: center;
      min-width: 0;
      padding: 0 0.42rem;
    }

    .page-form input {
      width: 3.35rem;
    }

    .reader-layout--sidebar {
      grid-template-columns: minmax(0, 1fr);
    }

    .page-sidebar {
      position: absolute;
      inset: 0 auto 0 0;
      z-index: 5;
      width: min(14.5rem, 78vw);
      box-shadow: 16px 0 34px rgba(15, 23, 42, 0.18);
    }

    .thumb-canvas-shell {
      min-height: 10.85rem;
    }

    .continuous-pages {
      gap: 0.72rem;
      padding: 0.72rem 0.5rem;
    }

    .page-card {
      min-width: 15rem;
      min-height: 20rem;
    }

    .page-card--sized {
      min-width: 0;
      min-height: 0;
    }
  }
</style>
